import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import {
  StudentVurderingDto as StudentVurderingDtoUHA,
  StudentVurderingDtoVurderingsstatusDtoEnum as VurderingsstatusEnumUHA,
} from '../api/generated/studiested-generated-api';
import {
  StudentVurderingDto as StudentVurderingDtoPSA,
  StudentVurderingDtoVurderingsstatusDtoEnum as VurderingsstatusEnumPSA,
} from '../api/generated/praksissted-generated-api';
import translations from './evaluationstudenttable.i18n';
import Button from '../components/button';
import Spinner from '../components/spinner/spinner';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import { UserType } from '../util/userType';
import useTableSearch from '../components/table/useTableSearch';
import * as css from './evaluationstudenttable.module.scss';
import { EvaluationType } from './evaluationtabs';

enum TableHeaders {
  Student = 'Student',
  Studiested = 'Studiested',
  Kontakt = 'Kontakt',
  Status = 'Status',
  Handling = 'Handling',
}

type TableHeaderToString = {
  [key in TableHeaders]: string;
};

type TableHeaderForSearch = Pick<
  TableHeaderToString,
  TableHeaders.Student | TableHeaders.Studiested
>;

const TableHeadersSort = [
  TableHeaders.Student,
  TableHeaders.Studiested,
  TableHeaders.Status,
];

const EvaluationStudentTableWrapper: React.FC<
  React.PropsWithChildren<{
    evaluationType: EvaluationType;
    userType: UserType;
  }>
> = ({ evaluationType, userType }) => {
  const { activityId } = useParams<{ activityId: string }>();
  const { vurderingStudentApi: UHVurderingApi } =
    useContext(StudiestedApiContext);
  const { vurderingStudentApi: PSVurderingApi } = useContext(
    PraksisstedApiContext,
  );
  const { data, loading } = useLoader(() => {
    if (!activityId) return Promise.reject('No activity id');

    const api = userType === UserType.UH ? UHVurderingApi : PSVurderingApi;
    return evaluationType === EvaluationType.MIDTERM
      ? api.vurderingAktivitetIdMidtveisStudenterGet({
          pathParams: { aktivitetId: activityId },
        })
      : api.vurderingAktivitetIdSluttStudenterGet({
          pathParams: { aktivitetId: activityId },
        });
  }, [activityId, UHVurderingApi, PSVurderingApi, evaluationType]);

  if (loading) return <Spinner />;
  if (!data || !activityId) return <></>;

  return (
    <EvaluationStudentTable
      vurderingstatuser={data.studenter}
      evaluationType={evaluationType}
      aktivitetId={activityId}
    />
  );
};

// Exported for tests
export const EvaluationStudentTable: React.FC<
  React.PropsWithChildren<{
    vurderingstatuser: StudentVurderingDtoUHA[] | StudentVurderingDtoPSA[];
    evaluationType: EvaluationType;
    aktivitetId: string;
  }>
> = ({ vurderingstatuser, evaluationType, aktivitetId }) => {
  const navigate = useNavigate();

  const [searchWords, setSearchWords] = useState<TableHeaderForSearch>({
    [TableHeaders.Student]: '',
    [TableHeaders.Studiested]: '',
  });

  const { formatMessage, locale } = useIntl();
  const translateStatusToLabel = useCallback(
    (status: VurderingsstatusEnumUHA | VurderingsstatusEnumPSA) => {
      switch (status) {
        case VurderingsstatusEnumUHA.Aktiv:
        case VurderingsstatusEnumPSA.Aktiv:
          return formatMessage({ id: translations.statusAktiv.id });
        case VurderingsstatusEnumUHA.AvbruttPraksis:
        case VurderingsstatusEnumPSA.AvbruttPraksis:
          return formatMessage({ id: translations.statusAvbruttPraksis.id });
        case VurderingsstatusEnumUHA.PraksisFullfrt:
        case VurderingsstatusEnumPSA.PraksisFullfrt:
          return formatMessage({ id: translations.statusPraksisFullfoert.id });
        default:
          return '';
      }
    },
    [formatMessage],
  );

  const labels = useMemo(
    () => ({
      student: formatMessage({ id: translations.student.id }),
      studiested: formatMessage({ id: translations.studiested.id }),
      kontakt: formatMessage({ id: translations.kontakt.id }),
      status: formatMessage({ id: translations.status.id }),
      handling: formatMessage({ id: translations.handling.id }),
    }),
    [formatMessage],
  );

  const headersWithTranslations: TableHeaderToString = useMemo(
    () => ({
      [TableHeaders.Student]: labels.student,
      [TableHeaders.Studiested]: labels.studiested,
      [TableHeaders.Kontakt]: labels.kontakt,
      [TableHeaders.Status]: labels.status,
      [TableHeaders.Handling]: labels.handling,
    }),
    [labels],
  );

  const initActiveCol = useMemo(
    () => Object.keys(headersWithTranslations).indexOf(TableHeaders.Student),
    [headersWithTranslations],
  );

  const [activeCol, setActiveCol] = useState(initActiveCol);

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = useCallback(
    (
      col: number,
      a: StudentVurderingDtoUHA | StudentVurderingDtoPSA,
      b: StudentVurderingDtoUHA | StudentVurderingDtoPSA,
    ) => {
      switch (Object.keys(TableHeaders)[col]) {
        case TableHeaders.Student:
          return compare(
            `${a.student.etternavn}${a.student.fornavn}`,
            `${b.student.etternavn}${b.student.fornavn}`,
          );
        case TableHeaders.Studiested:
          return compare(a.studiestedNavn, b.studiestedNavn);
        case TableHeaders.Status:
          return compare(
            translateStatusToLabel(a.vurderingsstatus),
            translateStatusToLabel(b.vurderingsstatus),
          );
      }
      return 0;
    },
    [compare, translateStatusToLabel],
  );

  const sortVurderinger = useCallback(
    (
      col: number,
      ascending: boolean,
      studenterToSort: StudentVurderingDtoUHA[] | StudentVurderingDtoPSA[],
    ) =>
      [...studenterToSort].sort((a, b) =>
        ascending ? sort(col, b, a) : sort(col, a, b),
      ),
    [sort],
  );

  const [sortState, setSortState] = useState<{
    col: number;
    ascending: boolean;
  }>({ col: initActiveCol, ascending: false });

  const filteredVurderinger = useTableSearch(
    vurderingstatuser,
    [
      [
        searchWords[TableHeaders.Student],
        (studentVurdering: StudentVurderingDtoUHA | StudentVurderingDtoPSA) =>
          `${studentVurdering.student.etternavn}${studentVurdering.student.fornavn}`,
      ],
      [
        searchWords[TableHeaders.Studiested],
        (studentVurdering: StudentVurderingDtoUHA | StudentVurderingDtoPSA) =>
          studentVurdering.studiestedNavn,
      ],
    ],
    1,
  );

  const getRows = useCallback(
    (vurderinger: StudentVurderingDtoUHA[] | StudentVurderingDtoPSA[]) =>
      vurderinger.map((vurderingStatus) => {
        const navn = `${vurderingStatus.student.etternavn}, ${vurderingStatus.student.fornavn}`;
        const fsStudentId = vurderingStatus.student.fsStudentId;
        return (
          <TableRow key={vurderingStatus.studentVurderingId}>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Student]}
              className={css.studentCell}
            >
              {fsStudentId ? (
                <NavLink
                  to={`/myinstitution/studentbrukerprofil/${fsStudentId}`}
                >
                  {navn}
                </NavLink>
              ) : (
                navn
              )}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Studiested]}
              className={css.institutionCell}
            >
              {vurderingStatus.studiestedNavn}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Kontakt]}
              className={css.contactCell}
            >
              {`${vurderingStatus.student.epost ?? ''} ${
                vurderingStatus.student.telefon ?? ''
              }`.trim()}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Status]}
              className={css.statusCell}
            >
              {translateStatusToLabel(vurderingStatus.vurderingsstatus)}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Handling]}
              className={css.buttonCell}
            >
              <Button.Secondary
                role={'link'}
                onClick={() =>
                  navigate(
                    `/evaluation/${aktivitetId}/${
                      vurderingStatus.studentVurderingId
                    }/${
                      evaluationType === EvaluationType.FINAL
                        ? 'final'
                        : 'midterm'
                    }`,
                  )
                }
              >
                {formatMessage({
                  id:
                    evaluationType === EvaluationType.FINAL
                      ? translations.btnHandlingSlutt.id
                      : translations.btnHandlingMidt.id,
                })}
              </Button.Secondary>
            </TableCellTd>
          </TableRow>
        );
      }),
    [
      translateStatusToLabel,
      navigate,
      aktivitetId,
      formatMessage,
      evaluationType,
      headersWithTranslations,
    ],
  );

  const headers = useMemo(
    () =>
      Object.entries(headersWithTranslations).map(([type, title], index) => (
        <TableCellTh
          key={`${type}-${title}`}
          colIndex={index}
          activeCol={index === activeCol}
          setActiveCol={setActiveCol}
          sort={(col, ascending) => setSortState({ col, ascending })}
          canSort={TableHeadersSort.includes(type as TableHeaders)}
          search={(_col, value) =>
            setSearchWords({ ...searchWords, [type]: value })
          }
          canSearch={Object.keys(searchWords).includes(type)}
        >
          {title}
        </TableCellTh>
      )),
    [headersWithTranslations, activeCol, setSearchWords, searchWords],
  );

  const rows = useMemo(
    () =>
      getRows(
        sortVurderinger(
          sortState.col,
          sortState.ascending,
          filteredVurderinger,
        ),
      ),
    [filteredVurderinger, sortState, sortVurderinger, getRows],
  );

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow top>{headers}</TableRow>
        </TableHeader>
        <TableMain>{rows}</TableMain>
      </Table>
    </>
  );
};

export default EvaluationStudentTableWrapper;
